/**
 * Frame sampler vocabulary (ARD-1977).
 *
 * Every episode reports under the single key `perf.app.frame_episode`; the
 * surface and trigger are metadata so a dashboard groups by
 * `@context.metadata.surface` rather than needing one key per surface.
 *
 * The observer is global (one pair of listeners on `document`), so a surface
 * is not a code registration: it is the label `describeFrameTarget` derives
 * from the element that scrolled or transitioned. `FrameSurface` holds the
 * labels the product marks explicitly with `data-frame-surface`, spelled
 * exactly as they reach Datadog (the labeller keeps an authored token as
 * written); anything else reports under its own derived label. Nothing is
 * pinned from this list — the debug strip shows whatever has reported.
 */

/** The marker attribute an element carries to name itself as a surface. */
export const FRAME_SURFACE_ATTRIBUTE = 'data-frame-surface';

export const FrameSurface = {
  SESSION_LIST: 'session_list',
  SESSION_SIDEBAR_LIST: 'session_sidebar_list',
  NOTE_EDITOR: 'note_editor',
  SIDEBAR_PANEL: 'sidebar_panel',
  SESSION_DETAILS_PANEL: 'session_details_panel',
  DOCUMENT: 'document',
} as const;

export type KnownFrameSurface = (typeof FrameSurface)[keyof typeof FrameSurface];

/** Any label: a known constant or one derived from the DOM. */
export type FrameSurface = string;

/**
 * `scroll`: a scroller moved. `transition`: a layout CSS transition ran on the
 * element. `pointer` / `key`: the user pressed the primary button or a
 * navigation key; a fixed window from that input measures the re-render it
 * caused (a tab switch, a panel toggle), which no DOM event otherwise reports.
 * Interactions are named for the input that STARTS them, not for `click`:
 * activation commonly runs on `mousedown` or `focus`, before any click.
 */
export type FrameTrigger = 'scroll' | 'transition' | 'pointer' | 'key';

/** A frame gap above this is one dropped frame at 60 Hz. */
export const LONG_FRAME_MS = 33;

/** A frame gap above this reads as a freeze to the user. */
export const FROZEN_FRAME_MS = 100;

/**
 * The frame budget every episode is normalised against: one frame at 60 Hz.
 *
 * Why a fixed 60 and not the device's own refresh rate. The obvious ratio,
 * `long_frames / frames`, is unusable across a fleet because BOTH ends of it
 * move with the panel: `frames` is the rAF callback count, so a 120 Hz display
 * doubles the denominator, and a fixed 33 ms threshold takes one dropped frame
 * to trip at 60 Hz but three at 120 Hz. The same 100 ms stall in a one-second
 * episode scored 1.8 % on a 60 Hz panel, 1.4 % at 75 Hz and 0.9 % at 120 Hz —
 * a two-to-one spread from hardware alone, in the direction that makes the
 * desktop build (the thing this sampler exists to measure) look worse than a
 * browser on a ProMotion display.
 *
 * Measuring time spent beyond a 60 Hz budget removes the panel from both ends:
 * that same stall is 7.5 % everywhere (75 ms beyond `JANK_THRESHOLD_MS`). 60 is a floor rather than an arbitrary
 * reference — perceived smoothness saturates around it, so a 120 Hz device
 * delivering a steady 60 fps scores 0, which is the honest answer. The cost is
 * deliberate blindness above 60: a ProMotion slide from 120 to 70 fps reports
 * clean. For the freezes this sampler was built for (100 to 400 ms, ARD-1977)
 * that is the right trade; revisit it from `cadence_hz` if it stops being.
 */
export const FRAME_BUDGET_60_MS = 1000 / 60;

/**
 * Overshoot is charged only beyond this, per interval: `max(0, gap − 25)`,
 * the shape Total Blocking Time uses (ARD-1977 accuracy, design D4).
 *
 * Why a threshold above the 60 Hz frame. WebKit reports time at 1 ms
 * resolution, so a healthy 60 Hz frame reads 16 or 17 ms; charging every gap
 * over 16.67 gave an idle WebKit page a 2.12 % jank floor against 0.16 % on
 * Chromium — a bias against the desktop build, which runs on WebKit.
 *
 * Why continuous rather than a step to `gap − 16.67`. Common panels put a
 * whole number of frames almost exactly on 25 ms (3 at 120 Hz, 6 at 240 Hz,
 * 4 at 165 Hz, 2 at 75 Hz), and with a step, sub-millisecond jitter decided
 * whether the same gap paid 0 or ~9 ms. Here it pays at most the clock's
 * resolution.
 *
 * Why fixed in milliseconds and not scaled to the detected cadence. A stall
 * pays the same overshoot on every panel; scaling would bring back the panel
 * dependence `FRAME_BUDGET_60_MS` removed, and the cadence is 0 for short
 * episodes. Healthy frames at 60–240 Hz pay nothing on either engine.
 */
export const JANK_THRESHOLD_MS = 1.5 * FRAME_BUDGET_60_MS;
